import { motion, useReducedMotion } from "framer-motion";
import {
  Activity, BarChart3, Building2, CreditCard, Gift, HelpCircle, LayoutDashboard, Megaphone,
  Settings, ShoppingBag, UserCog, UserPlus, Users, UploadCloud, Wallet,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { SHELL } from "../../constants/testIds";
import { useAppData } from "../../contexts/AppDataContext";
import { hasPermission } from "../../lib/format";

// UI_STANDARDS.md B6/B7 + UX_MOTION.md "Nav": fixed 240px sidebar, entitlement-scoped items (a
// module toggled off does NOT appear — module independence), active item = solid scarlet fill
// that SLIDES between items (framer-motion shared layoutId), not a re-render blink.
// `permission`: UX-P04 — this shell is no longer super_admin-exclusive (the new
// `implementation_team` role lands here too, via a much narrower permission set), so a day-to-day
// Owner Cockpit item a role can't actually use (no staff.view, no pnl.view, ...) is hidden rather
// than rendering into a 403/empty page.
const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/app/dashboard", permission: "pnl.view" },
  { key: "members", label: "Members", icon: Users, path: "/app/members", permission: "member.view" },
  { key: "leads", label: "Leads", icon: UserPlus, path: "/app/leads", permission: "lead.view" },
  { key: "marketing", label: "Marketing", icon: Megaphone, path: "/app/marketing", permission: "marketing.view" },
  { key: "plans", label: "Memberships & Plans", icon: CreditCard, path: "/app/plans", permission: "plan.view" },
  { key: "money", label: "Money", icon: Wallet, path: "/app/money", permission: "pnl.view" },
  { key: "operations", label: "Operations", icon: Activity, path: "/app/operations", permission: "schedule.view" },
  { key: "staff", label: "Staff", icon: UserCog, path: "/app/staff", permission: "staff.view" },
  { key: "branches", label: "Branches", icon: Building2, path: "/app/branches", permission: "staff.view" },
  { key: "reports", label: "Reports", icon: BarChart3, path: "/app/reports", permission: "pnl.view" },
  { key: "revenue-extras", label: "Revenue Extras", icon: Gift, path: "/app/revenue-extras", module: "loyalty", permission: "pnl.view" },
  { key: "retail", label: "Retail", icon: ShoppingBag, path: "/app/retail", module: "retail", permission: "pnl.view" },
  { key: "migration", label: "Migration Studio", icon: UploadCloud, path: "/app/migration", permission: "migration.manage" },
  { key: "settings", label: "Settings", icon: Settings, path: "/app/settings", permission: "pnl.view" },
  { key: "help", label: "Help", icon: HelpCircle, path: "/app/help" },
];

export function Sidebar() {
  const { me, entitlements } = useAppData();
  const reducedMotion = useReducedMotion();
  const orgName = me?.organization_display_name || "FitFrikArena";
  const initials = orgName.slice(0, 2).toUpperCase();

  const visibleItems = NAV_ITEMS.filter((item) => (!item.module || entitlements[item.module] !== false) && (!item.permission || hasPermission(me, item.permission)));

  return (
    <aside data-testid={SHELL.sidebar} className="scarlet-sidebar w-[240px] shrink-0 h-screen sticky top-0 flex flex-col">
      <div className="flex items-center gap-3 px-5 py-6">
        <div
          data-testid={SHELL.logoMark}
          className="w-9 h-9 rounded-[10px] flex items-center justify-center text-white font-bold text-sm shrink-0"
          style={{ background: "var(--scarlet-accent)" }}
        >
          {initials}
        </div>
        <div className="overflow-hidden">
          <p className="font-bold text-[15px] leading-tight truncate" style={{ color: "var(--scarlet-text)" }}>{orgName}</p>
          <p className="eyebrow leading-tight">FitFrikArena</p>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => (
          <NavLink key={item.key} to={item.path} data-testid={SHELL.navItem(item.key)} className="block">
            {({ isActive }) => (
              <div className="relative">
                {isActive && (
                  <motion.div
                    layoutId="active-nav-pill"
                    className="absolute inset-0 rounded-[10px]"
                    style={{ background: "var(--scarlet-accent)" }}
                    transition={reducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  />
                )}
                <motion.div
                  whileHover={reducedMotion ? {} : { x: 2 }}
                  transition={{ duration: 0.14 }}
                  className="relative z-10 flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-medium scarlet-focusable"
                  style={{ color: isActive ? "#FFFFFF" : "var(--scarlet-nav-text)" }}
                >
                  <item.icon size={18} strokeWidth={1.7} className={reducedMotion ? "" : "transition-transform duration-150 group-hover:scale-[1.15]"} />
                  <span className={isActive ? "font-semibold" : ""}>{item.label}</span>
                </motion.div>
              </div>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
